import React from "react"
import { useQuery } from "react-query"
import { AxiosError } from "axios"
import { CandidatureModel, IHttpErrorResponseModel } from "@common-models/*"
import { toast } from "react-toastify"
import { getCandidatures } from "../../../../core/_requests"
import { useSearchResultsContext } from "../../CandidatsRechercheResults"
import MyAlert from "@common-utils/MyAlert"
import { useSearchResultsPreviewContext } from "../_CandidatsRechercheResultsPreview"
import GlobalHelper from "@common-helpers/GlobalHelper"

const REACT_QUERY_KEY_GET_CANDIDATURES_QUERY = "REACT_QUERY_KEY_GET_CANDIDATURES_QUERY"

export const CandidatsRechercheResultsPreviewHelpersCandidatures = () => {
     const globalContext = useSearchResultsContext()
     const previewContext = useSearchResultsPreviewContext()

     const candidaturesQuery = useQuery<CandidatureModel[], AxiosError>([REACT_QUERY_KEY_GET_CANDIDATURES_QUERY, globalContext.itemInPreview?.selectedItem.candidat.id], () => {
          return getCandidatures(globalContext.itemInPreview?.selectedItem.candidat.id as number)
               .then(r => r.data)
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data
                    toast.error(error?.detail, { autoClose: false })
                    throw e
               })
               .finally(() => {
                    previewContext.setIsPreviewBodyLoading(false)
                    globalContext.setItemInPreview(prev => (prev ? { ...prev, isSelectedItemLoading: false } : null))
               })
     })

     const LoadingState = () => (
          <div className="p-4">
               {[1, 2, 3].map(i => (
                    <div key={i} className="d-flex align-items-center mb-4 animate-pulse">
                         <div className="bg-light rounded w-100px h-20px me-4"></div>
                         <div className="bg-light rounded w-200px h-20px me-auto"></div>
                         <div className="bg-light rounded w-80px h-20px"></div>
                    </div>
               ))}
          </div>
     )

     const EmptyState = () => (
          <div className="d-flex flex-column align-items-center justify-content-center py-10 text-muted">
               <i className="bi bi-inbox fs-2x mb-3 text-gray-400" />
               <span className="fs-6 fw-bold text-gray-500">Aucune candidature trouvée</span>
          </div>
     )

     const CandidatureItem = ({ item }: { item: CandidatureModel }) => {
          const formattedDate = item.date ? GlobalHelper.firstLetterUppercase(item.date.format("dddd Do MMM YYYY")) : "-"

          return (
               <tr className="border-bottom border-gray-100">
                    {/* Date Column */}
                    <td className="ps-4">
                         <span className="text-gray-800 fw-bold d-block fs-6">{formattedDate}</span>
                    </td>

                    {/* Annonce Column */}
                    <td>
                         <div className="d-flex flex-column">
                              <span className="text-dark fw-bolder text-hover-primary fs-6 cursor-pointer mb-1">{item.annonce?.intitule || "Annonce sans titre"}</span>
                              {item.annonce?.personnel?.prenomNom && <span className="text-muted fw-semibold d-block fs-7">Recruteur: {item.annonce.personnel.prenomNom}</span>}
                         </div>
                    </td>

                    {/* Job Board Column - Styled as a Badge */}
                    <td className="text-end pe-4">
                         <span className={`badge badge-light-primary fw-bolder px-4 py-2`}>{item.jobBoard || "N/A"}</span>
                    </td>
               </tr>
          )
     }

     return (
          <div className="preview-height">
               {/* In case of error */}
               {candidaturesQuery.error ? (
                    <MyAlert type="danger" classNames="mb-5">
                         {candidaturesQuery.error.response?.data.detail || "Une erreur est survenue."}
                    </MyAlert>
               ) : (
                    <>
                         {candidaturesQuery.isLoading && <LoadingState />}

                         {candidaturesQuery.isSuccess && (
                              <>
                                   {candidaturesQuery.data.length === 0 ? (
                                        <EmptyState />
                                   ) : (
                                        <table className="table table-rounded table-striped gy-7 gs-7 m-0 p-0">
                                             <thead className="fw-bold fs-6 text-gray-800 border-bottom border-gray-200">
                                                  <tr>
                                                       <th>Date de candidature</th>
                                                       <th>Annonce</th>
                                                       <th>Job board</th>
                                                  </tr>
                                             </thead>
                                             <tbody>
                                                  {candidaturesQuery.data.map((item: CandidatureModel, index) => (
                                                       <CandidatureItem item={item} key={item.id || index} />
                                                  ))}
                                             </tbody>
                                        </table>
                                   )}
                              </>
                         )}
                    </>
               )}
          </div>
     )
}
